import { z } from 'zod';
import { pcmpReportSearchShape, type PcmpReportFilters } from './pcmpReports.types';

/** The report's filters, plus the MIS member and the Not Verified reason. */
export interface PcmpMisStatusReportParams extends PcmpReportFilters {
  misUserId?: number | undefined;
  /** Narrows the Not Verified side only, so the Verified column empties. */
  subStatus?: string | undefined;
}

export const pcmpMisStatusReportSearchSchema = z.object({
  ...pcmpReportSearchShape,
  misUserId: z.number().int().positive().optional().catch(undefined),
  subStatus: z.string().optional().catch(undefined),
});

export type PcmpMisStatusReportSearch = z.infer<typeof pcmpMisStatusReportSearchSchema>;

export const pcmpMisReportMembersSchema = z.array(
  z.object({
    userId: z.coerce.number(),
    name: z.string(),
    hrId: z.string().nullable(),
  }),
);

export type PcmpMisReportMember = z.infer<typeof pcmpMisReportMembersSchema>[number];

const lineSchema = z.object({
  member: z.object({
    userId: z.coerce.number(),
    name: z.string(),
    hrId: z.string().nullable(),
  }),
  verified: z.number(),
  notVerified: z.number(),
  /** Not Verified decisions per reason code: only reasons that occurred. */
  reasons: z.record(z.string(), z.number()),
  total: z.number(),
});

export type PcmpMisStatusLine = z.infer<typeof lineSchema>;

export const pcmpMisStatusReportSchema = z.object({
  reasons: z.array(z.object({ code: z.string(), label: z.string() })),
  lines: z.array(lineSchema),
  total: lineSchema,
  verifiedTotal: z.number(),
  notVerifiedTotal: z.number(),
  totalUpdates: z.number(),
  memberCount: z.number(),
  projectLabel: z.string(),
  periodLabel: z.string(),
  /** The window actually used: blank dates default to today. */
  dateFrom: z.string().nullable(),
  dateTo: z.string().nullable(),
  reportDate: z.string(),
});

export type PcmpMisStatusReport = z.infer<typeof pcmpMisStatusReportSchema>;
